import React, { useState, useRef, useId } from 'react';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  placement?: TooltipPlacement;
  delay?: number;
  disabled?: boolean;
}

const placementStyles: Record<TooltipPlacement, string> = {
  top:    'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left:   'right-full top-1/2 -translate-y-1/2 mr-2',
  right:  'left-full top-1/2 -translate-y-1/2 ml-2',
};

export function Tooltip({
  content,
  children,
  placement = 'top',
  delay = 400,
  disabled = false,
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  const show = () => {
    if (disabled) return;
    timerRef.current = setTimeout(() => setVisible(true), delay);
  };

  const hide = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
  };

  const child = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      show();
      children.props.onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hide();
      children.props.onMouseLeave?.(e);
    },
    onFocus: (e: React.FocusEvent) => {
      show();
      children.props.onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent) => {
      hide();
      children.props.onBlur?.(e);
    },
    'aria-describedby': visible ? tooltipId : undefined,
  });

  return (
    <span className="relative inline-flex">
      {child}
      {visible && content && (
        <span
          id={tooltipId}
          role="tooltip"
          className={[
            'absolute z-50 whitespace-nowrap',
            'bg-[var(--bg-elevated)] border border-[var(--border-default)]',
            'rounded-[var(--radius-md)] shadow-[var(--shadow-raised)]',
            'px-2.5 py-1.5',
            'text-[12px] text-[var(--text-primary)] leading-[1.45]',
            'pointer-events-none select-none',
            'animate-[fadeIn_100ms_ease-out]',
            placementStyles[placement],
          ].join(' ')}
        >
          {content}
        </span>
      )}
    </span>
  );
}
